class ToastManager {
    constructor() {
        this.toasts = Array.from(document.querySelectorAll('.mda-toast'));
        if (!this.toasts.length) return;

        this.toastMap = new Map();
        this.toasts.forEach((toast) => {
            const id = toast.getAttribute('data-toast-id') || '';
            if (!id) return;
            this.toastMap.set(id, this.createToastApi(toast));
        });

        this.isCart = document.body.classList.contains('woocommerce-cart');
        this.isAccount = document.body.classList.contains('woocommerce-account');

        this.lastMessage = '';
        this.lastShownAt = 0;
        this.pendingNotices = new Set();
        this.noticeFlushRaf = null;
        this.armCartTimer = null;
        this.observer = null;

        this.bindEvents();
        this.observeNotices();
        this.consumeNotices();
    }

    createToastApi(toast) {
        if (toast.parentElement !== document.body) {
            document.body.appendChild(toast);
        }

        const content = toast.querySelector('.mda-toast__content') || toast;
        const closeButton = toast.querySelector('.mda-toast__close');
        const defaultText = toast.getAttribute('data-toast-text') || '';
        const hideDuration = 4800;
        let hideTimer = null;

        if (closeButton) {
            closeButton.addEventListener('click', () => {
                if (hideTimer) {
                    window.clearTimeout(hideTimer);
                }
                toast.classList.remove('is-visible');
                toast.classList.remove('mda-toast--animating');
                toast.style.pointerEvents = '';
            });
        }

        const show = (text, options = {}) => {
            const message = text || defaultText;
            if (message) {
                if (options.html) {
                    content.innerHTML = this.normalizeToastHtml(message);
                } else {
                    content.textContent = message;
                }
            }

            toast.classList.add('is-visible');
            toast.classList.remove('mda-toast--animating');
            toast.style.pointerEvents = 'auto';
            toast.style.setProperty('--toast-duration', `${hideDuration}ms`);
            window.requestAnimationFrame(() => {
                toast.classList.add('mda-toast--animating');
            });

            if (hideTimer) {
                window.clearTimeout(hideTimer);
            }

            hideTimer = window.setTimeout(() => {
                toast.classList.remove('is-visible');
                toast.classList.remove('mda-toast--animating');
                toast.style.pointerEvents = '';
            }, hideDuration);
        };

        return { show };
    }

    normalizeToastHtml(html) {
        const temp = document.createElement('div');
        temp.innerHTML = html || '';

        temp.querySelectorAll('a').forEach(link => {
            link.classList.remove('button', 'wc-forward');
        });

        temp.querySelectorAll('p').forEach(paragraph => {
            const fragment = document.createDocumentFragment();
            while (paragraph.firstChild) {
                fragment.appendChild(paragraph.firstChild);
            }
            paragraph.replaceWith(fragment);
        });

        return temp.innerHTML.trim();
    }

    getNoticeToastId() {
        if (this.isCart) return 'cart';
        if (this.isAccount) return 'account';
        return '';
    }

    extractNoticeText(notice) {
        if (!notice) return '';
        return notice.textContent.replace(/\s+/g, ' ').trim();
    }

    extractNoticeHtml(notice) {
        if (!notice) return '';
        return notice.innerHTML.trim();
    }

    handleNotice(notice) {
        const toastId = this.getNoticeToastId();
        const toastApi = toastId ? this.toastMap.get(toastId) : null;
        if (!toastApi) return;

        const html = this.extractNoticeHtml(notice);
        const message = this.extractNoticeText(notice);
        if (!message) return;
        const now = Date.now();
        if (message === this.lastMessage && now - this.lastShownAt < 500) {
            return;
        }
        this.lastMessage = message;
        this.lastShownAt = now;
        toastApi.show(html || message, { html: Boolean(html) });
    }

    armCartNoticeListener() {
        if (!this.isCart) return;
        if (this.armCartTimer) {
            window.clearTimeout(this.armCartTimer);
        }

        this.armCartTimer = window.setTimeout(() => {
            this.consumeNotices();
        }, 120);
    }

    bindEvents() {
        document.body.addEventListener('sp:toast', event => {
            const detail = event?.detail || {};
            const toastId = detail.id || 'cart';
            const toastApi = this.toastMap.get(toastId);
            if (!toastApi) return;
            toastApi.show(detail.text || '', { html: Boolean(detail.html) });
        });

        if (window.jQuery) {
            window.jQuery(document.body).on('added_to_cart', () => {
                const toastApi = this.toastMap.get('cart');
                if (toastApi) {
                    toastApi.show();
                }
            });

            if (this.isCart) {
                window.jQuery(document.body).on(
                    'updated_wc_div item_removed_from_classic_cart',
                    () => {
                        this.armCartNoticeListener();
                    }
                );
                window.jQuery(document.body).on(
                    'applied_coupon removed_coupon',
                    () => {
                        this.armCartNoticeListener();
                    }
                );
            }
        }

        if (this.isCart) {
            document.body.addEventListener('change', (event) => {
                if (!event.target.closest('.woocommerce-cart-form')) return;
                if (!event.target.matches('.cart_item input, .cart_item select, .cart_item textarea')) return;
                this.armCartNoticeListener();
            });
            document.body.addEventListener('click', (event) => {
                const cartAction = event.target.closest(
                    '.woocommerce-cart-form [name="update_cart"], ' +
                        '.woocommerce-cart-form [name="apply_coupon"], ' +
                        '.woocommerce-cart-form .product-remove > a'
                );
                if (!cartAction) return;
                this.armCartNoticeListener();
            });
        }

        document.body.addEventListener('submit', (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;
            if (!form.matches('form.cart')) return;
            const toastApi = this.toastMap.get('cart');
            if (toastApi) {
                toastApi.show();
            }
        });
    }

    observeNotices() {
        if (!this.isCart && !this.isAccount) {
            return;
        }

        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) return;
                    if (node.matches?.('.woocommerce-message, .woocommerce-error, .woocommerce-info, .coupon-error-notice')) {
                        this.enqueueNotice(node);
                    } else if (node.querySelector) {
                        const notice = node.querySelector('.woocommerce-message, .woocommerce-error, .woocommerce-info, .coupon-error-notice');
                        if (notice) {
                            this.enqueueNotice(notice);
                        }
                    }
                });
            });
        });

        const roots = this.getNoticeObserverRoots();
        roots.forEach((root) => {
            this.observer.observe(root, { childList: true, subtree: true });
        });
    }

    getNoticeObserverRoots() {
        const roots = new Set();
        const selectors = [
            '.woocommerce-notices-wrapper',
            '.woocommerce-cart-form',
            '.cart-collaterals',
            '.woocommerce-MyAccount-content',
            '.woocommerce-account',
        ];

        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((node) => {
                if (node instanceof HTMLElement) {
                    roots.add(node);
                }
            });
        });

        if (!roots.size) {
            roots.add(document.body);
        }

        return Array.from(roots);
    }

    enqueueNotice(notice) {
        if (!(notice instanceof HTMLElement)) {
            return;
        }

        this.pendingNotices.add(notice);

        if (this.noticeFlushRaf !== null) {
            return;
        }

        this.noticeFlushRaf = window.requestAnimationFrame(() => {
            this.noticeFlushRaf = null;
            this.flushQueuedNotices();
        });
    }

    flushQueuedNotices() {
        if (!this.pendingNotices.size) {
            return;
        }

        const queue = Array.from(this.pendingNotices);
        this.pendingNotices.clear();

        queue.forEach((notice) => this.handleNotice(notice));
    }

    consumeNotices() {
        const notices = document.querySelectorAll(
            '.woocommerce-message, .woocommerce-error, .woocommerce-info, .coupon-error-notice'
        );
        notices.forEach((notice) => this.handleNotice(notice));
    }

}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new ToastManager();
    });
} else {
    new ToastManager();
}
